import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {AdapterMoment, Box, ButtonBase, DateCalendar, LocalizationProvider, Popover, Stack, Typography} from 'views/components';
import {CalendarTodayOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';

import CohortSection from './sections/cohort/CohortSection';
import FunnelBreakdownSection from './sections/funnel_breakdown/FunnelBreakdownSection';
import KeyMetricsSection from './sections/key_metrics/KeyMetricsSection';

import backend from 'utils/backend';
import moment from 'moment-timezone';

const LeadFunnel = (props) => {
  const {
    statusProgressWrap: progressWrap,
  } = props;

  const timezone = 'America/Toronto';
  const latestAvailableDate = React.useMemo(() => {
    return moment.tz(timezone).subtract(1, 'day').startOf('day');
  }, [timezone]);

  const [comparisonMode, setComparisonMode] = React.useState('WEEKLY');
  const [selectedCurrentDate, setSelectedCurrentDate] = React.useState(latestAvailableDate.format('YYYY-MM-DD'));
  const [datePickerAnchorEl, setDatePickerAnchorEl] = React.useState(null);
  const [stageMetricsState, setStageMetricsState] = React.useState({
    isLoading: true,
    hasLoadedOnce: false,
    errorMessage: '',
    metricsByDate: {},
    fetchedDates: {},
    currentDate: '',
    stageComparisonDates: [],
  });
  const [logMetricsState, setLogMetricsState] = React.useState({
    isLoading: true,
    hasLoadedOnce: false,
    errorMessage: '',
    metricsByDate: {},
    fetchedDates: {},
    keyMetricsCurrentPeriodDates: [],
    keyMetricsPreviousPeriodDates: [],
    keyMetricsGraphDates: [],
  });
  const stageList = React.useMemo(() => {
    const stalledColor = translate('client.Status.STALLED.ColorDark');
    const closedLostColor = translate('client.Status.CLOSED_LOST.ColorDark');

    return [
      {key: 'LEAD', label: translate('client.Status.LEAD.Title'), color: translate('client.Status.LEAD.ColorDark')},
      {key: 'DETAILS_NEEDED', label: translate('client.Status.DETAILS_NEEDED.Title'), color: translate('client.Status.DETAILS_NEEDED.ColorDark')},
      {key: 'CG_SEARCH', label: translate('client.Status.CG_SEARCH.Title'), color: translate('client.Status.CG_SEARCH.ColorDark')},
      {key: 'CG_SEARCH_ACTIVE', label: translate('client.Status.CG_SEARCH_ACTIVE.Title'), color: translate('client.Status.CG_SEARCH_ACTIVE.ColorDark')},
      {key: 'CG_PROFILE_SENT', label: translate('client.Status.CG_PROFILE_SENT.Title'), color: translate('client.Status.CG_PROFILE_SENT.ColorDark')},
      {key: 'MEET_GREET', label: translate('client.Status.MEET_GREET.Title'), color: translate('client.Status.MEET_GREET.ColorDark')},
      {key: 'ACTIVE', label: translate('client.Status.ACTIVE.Title'), color: translate('client.Status.ACTIVE.ColorDark')},
      {key: 'STALLED', label: translate('client.Status.STALLED.Title'), color: stalledColor, rowBackgroundColor: `${stalledColor}63`},
      {
        key: 'CLOSED_LOST',
        label: translate('client.Status.CLOSED_LOST.Title'),
        color: closedLostColor,
        rowBackgroundColor: `${closedLostColor}63`,
      },
    ];
  }, []);
  const comparisonWindow = React.useMemo(() => {
    const currentDateMoment = moment.tz(selectedCurrentDate, 'YYYY-MM-DD', timezone).startOf('day');
    const currentDate = currentDateMoment.format('YYYY-MM-DD');
    const previousStageDate = comparisonMode === 'DAILY' ?
      currentDateMoment.clone().subtract(1, 'day').format('YYYY-MM-DD') :
      currentDateMoment.clone().subtract(7, 'day').format('YYYY-MM-DD');

    if (comparisonMode === 'DAILY') {
      const keyMetricsGraphDates = Array.from({length: 8}, (_, index) => {
        return currentDateMoment.clone().subtract(7 - index, 'day').format('YYYY-MM-DD');
      });

      return {
        currentDate,
        stageComparisonDates: [previousStageDate],
        keyMetricsCurrentPeriodDates: [currentDate],
        keyMetricsPreviousPeriodDates: [previousStageDate],
        keyMetricsGraphDates,
      };
    }

    const keyMetricsCurrentPeriodDates = Array.from({length: 8}, (_, index) => {
      return currentDateMoment.clone().subtract(7 - index, 'day').format('YYYY-MM-DD');
    });
    const keyMetricsPreviousPeriodDates = Array.from({length: 8}, (_, index) => {
      return currentDateMoment.clone().subtract(15 - index, 'day').format('YYYY-MM-DD');
    });

    return {
      currentDate,
      stageComparisonDates: [previousStageDate],
      keyMetricsCurrentPeriodDates,
      keyMetricsPreviousPeriodDates,
      keyMetricsGraphDates: keyMetricsCurrentPeriodDates,
    };
  }, [comparisonMode, selectedCurrentDate, timezone]);

  const cohortDates = React.useMemo(() => {
    const currentDateMoment = moment.tz(selectedCurrentDate, 'YYYY-MM-DD', timezone).startOf('day');

    return Array.from({length: 8}, (_, index) => {
      return currentDateMoment.clone().subtract(7 - index, 'day').format('YYYY-MM-DD');
    });
  }, [selectedCurrentDate, timezone]);

  const cohortCurrentDate = cohortDates[cohortDates.length - 1] || '';
  const cohortPreviousDates = cohortDates.slice(0, -1);

  // Build the unique date sets needed for stage and log metrics to be requested.
  const requestedStageMetricDates = React.useMemo(() => {
    return Array.from(new Set([...cohortDates, comparisonWindow.currentDate, ...comparisonWindow.stageComparisonDates]));
  }, [cohortDates, comparisonWindow.currentDate, comparisonWindow.stageComparisonDates]);
  const requestedLogMetricDates = React.useMemo(() => {
    return Array.from(
        new Set([
          ...comparisonWindow.keyMetricsCurrentPeriodDates,
          ...comparisonWindow.keyMetricsPreviousPeriodDates,
          ...comparisonWindow.keyMetricsGraphDates,
        ]),
    );
  }, [comparisonWindow.keyMetricsCurrentPeriodDates, comparisonWindow.keyMetricsPreviousPeriodDates, comparisonWindow.keyMetricsGraphDates]);

  React.useEffect(() => {
    let isActive = true;

    const loadMetrics = async () => {
      const {
        currentDate,
        stageComparisonDates,
        keyMetricsCurrentPeriodDates,
        keyMetricsPreviousPeriodDates,
        keyMetricsGraphDates,
      } = comparisonWindow;
      const stageMetricDatesToFetch = requestedStageMetricDates.filter((date) => !stageMetricsState.fetchedDates[date]);
      const logMetricDatesToFetch = requestedLogMetricDates.filter((date) => !logMetricsState.fetchedDates[date]);

      setStageMetricsState((currentState) => ({
        ...currentState,
        isLoading: stageMetricDatesToFetch.length > 0,
        errorMessage: '',
      }));
      setLogMetricsState((currentState) => ({
        ...currentState,
        isLoading: logMetricDatesToFetch.length > 0,
        errorMessage: '',
      }));

      if (!stageMetricDatesToFetch.length) {
        setStageMetricsState((currentState) => ({
          ...currentState,
          currentDate,
          stageComparisonDates,
        }));
      }

      if (!logMetricDatesToFetch.length) {
        setLogMetricsState((currentState) => ({
          ...currentState,
          keyMetricsCurrentPeriodDates,
          keyMetricsPreviousPeriodDates,
          keyMetricsGraphDates,
        }));
      }

      // Optimization: do not need to fetch for dates we already fetched
      if (!stageMetricDatesToFetch.length && !logMetricDatesToFetch.length) {
        return;
      }

      await progressWrap(async () => {
        const [stageMetricsResult, logMetricsResult] = await Promise.allSettled([
          stageMetricDatesToFetch.length ? backend.metrics.queryDailyClientMetrics(stageMetricDatesToFetch) : Promise.resolve(null),
          logMetricDatesToFetch.length ? backend.metrics.queryDailyClientLogsMetrics(logMetricDatesToFetch) : Promise.resolve(null),
        ]);
        const syncFetchedMetrics = (setState, datesToFetch, result, fallbackErrorMessage, extraState) => {
          if (!datesToFetch.length) {
            return;
          }

          if (result.status === 'fulfilled') {
            setState((currentState) => {
              const metricsByDate = {
                ...currentState.metricsByDate,
              };

              result.value.metrics.forEach((metric) => {
                metricsByDate[metric.metricDate] = metric;
              });

              return {
                ...currentState,
                isLoading: false,
                hasLoadedOnce: true,
                errorMessage: '',
                metricsByDate,
                fetchedDates: {
                  ...currentState.fetchedDates,
                  ...datesToFetch.reduce((nextFetchedDates, date) => {
                    nextFetchedDates[date] = true;

                    return nextFetchedDates;
                  }, {}),
                },
                ...extraState,
              };
            });

            return;
          }

          setState((currentState) => ({
            ...currentState,
            isLoading: false,
            errorMessage: result.reason?.message || fallbackErrorMessage,
          }));
        };

        if (!isActive) {
          return;
        }

        syncFetchedMetrics(
            setStageMetricsState,
            stageMetricDatesToFetch,
            stageMetricsResult,
            translate('dashboardWidgets.ClientStageMetrics.LoadError'),
            {
              currentDate,
              stageComparisonDates,
            },
        );
        syncFetchedMetrics(
            setLogMetricsState,
            logMetricDatesToFetch,
            logMetricsResult,
            translate('metrics.LeadFunnel.KeyMetrics.LoadError'),
            {
              keyMetricsCurrentPeriodDates,
              keyMetricsPreviousPeriodDates,
              keyMetricsGraphDates,
            },
        );
      });
    };

    loadMetrics();

    return () => {
      isActive = false;
    };
  }, [comparisonWindow, progressWrap, requestedLogMetricDates, requestedStageMetricDates]);

  const formattedDateRange = React.useMemo(() => {
    const currentDateMoment = moment.tz(selectedCurrentDate, 'YYYY-MM-DD', timezone);

    if (comparisonMode === 'DAILY') {
      return currentDateMoment.format('MMM D, YYYY');
    }

    const startDate = currentDateMoment.clone().subtract(7, 'day');
    const endDate = currentDateMoment;

    return `${startDate.format('MMM D')} - ${endDate.format('MMM D, YYYY')}`;
  }, [comparisonMode, selectedCurrentDate, timezone]);

  const stageMissingDates = React.useMemo(() => {
    const missingDates = [];

    if (
      stageMetricsState.currentDate &&
      !stageMetricsState.metricsByDate[stageMetricsState.currentDate]
    ) {
      missingDates.push(moment.tz(stageMetricsState.currentDate, 'YYYY-MM-DD', timezone).format('MMM DD, YYYY'));
    }

    stageMetricsState.stageComparisonDates.forEach((date) => {
      if (!stageMetricsState.metricsByDate[date]) {
        missingDates.push(moment.tz(date, 'YYYY-MM-DD', timezone).format('MMM DD, YYYY'));
      }
    });

    return missingDates;
  }, [stageMetricsState.currentDate, stageMetricsState.metricsByDate, stageMetricsState.stageComparisonDates]);

  const stageRows = React.useMemo(() => {
    const currentMetric = stageMetricsState.metricsByDate[stageMetricsState.currentDate] || null;
    let previousMetric = null;

    if (stageMetricsState.stageComparisonDates.length) {
      previousMetric = stageMetricsState.metricsByDate[stageMetricsState.stageComparisonDates[0]] || null;
    }
    const currentStatusCount = currentMetric?.statusCount || {};
    const previousStatusCount = previousMetric?.statusCount || {};

    return stageList.map((stage) => {
      const nowCount = currentMetric ? currentStatusCount[stage.key]?.length || 0 : null;
      const prevCount = previousMetric ? previousStatusCount[stage.key]?.length || 0 : null;
      let delta = null;

      if (nowCount !== null && prevCount !== null) {
        delta = nowCount - prevCount;
      }

      return {
        ...stage,
        nowCount,
        prevCount,
        delta,
      };
    });
  }, [stageList, stageMetricsState.currentDate, stageMetricsState.metricsByDate, stageMetricsState.stageComparisonDates]);

  const stageDistributionMax = React.useMemo(() => {
    const includedStageRows = stageRows.filter((stage) => {
      return stage.key !== 'CLOSED_LOST';
    });
    const includedStageCounts = includedStageRows.map((stage) => {
      return stage.nowCount;
    });
    const currentStageCounts = includedStageCounts.filter((count) => {
      return count !== null;
    });

    return Math.max(1, ...currentStageCounts);
  }, [stageRows]);

  const keyMetricCards = React.useMemo(() => {
    const buildMetric = (label, filterFn) => {
      const currentSeries = logMetricsState.keyMetricsGraphDates.map((date) => {
        const clientRnSet = new Set();

        (logMetricsState.metricsByDate[date]?.clientLogs || []).forEach((clientLog) => {
          if (filterFn(clientLog)) {
            clientRnSet.add(clientLog.clientRn);
          }
        });

        return clientRnSet.size;
      });
      const currentClientRnSet = new Set();
      const previousClientRnSet = new Set();

      logMetricsState.keyMetricsCurrentPeriodDates.forEach((date) => {
        (logMetricsState.metricsByDate[date]?.clientLogs || []).forEach((clientLog) => {
          if (filterFn(clientLog)) {
            currentClientRnSet.add(clientLog.clientRn);
          }
        });
      });

      logMetricsState.keyMetricsPreviousPeriodDates.forEach((date) => {
        (logMetricsState.metricsByDate[date]?.clientLogs || []).forEach((clientLog) => {
          if (filterFn(clientLog)) {
            previousClientRnSet.add(clientLog.clientRn);
          }
        });
      });

      const currentCount = currentClientRnSet.size;
      const previousCount = previousClientRnSet.size;
      const delta = currentCount - previousCount;
      let deltaPercentage = 0;

      if (previousCount === 0 && currentCount > 0) {
        deltaPercentage = 100;
      } else if (previousCount > 0) {
        deltaPercentage = (delta / previousCount) * 100;
      }

      return {
        label,
        currentCount,
        previousCount,
        delta,
        deltaPercentage,
        series: currentSeries,
      };
    };

    return [
      buildMetric(
          translate('metrics.LeadFunnel.KeyMetrics.Leads'),
          (clientLog) => clientLog.oldStatus == null,
      ),
      buildMetric(
          translate('metrics.LeadFunnel.KeyMetrics.MeetGreet'),
          (clientLog) => clientLog.newStatus === 'MEET_GREET',
      ),
      buildMetric(
          translate('metrics.LeadFunnel.KeyMetrics.Starts'),
          (clientLog) => clientLog.newStatus === 'ACTIVE',
      ),
    ];
  }, [
    logMetricsState.keyMetricsCurrentPeriodDates,
    logMetricsState.metricsByDate,
    logMetricsState.keyMetricsPreviousPeriodDates,
    logMetricsState.keyMetricsGraphDates,
  ]);

  return (
    <Box
      sx={{
        minHeight: '100%',
        backgroundColor: '#F9F8F6',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '1160px',
          mx: 'auto',
          px: {xs: 2, md: 4},
          py: {xs: 3, md: 5.5},
        }}
      >
        <Stack spacing={3}>
          <Stack
            direction={{xs: 'column', lg: 'row'}}
            justifyContent='space-between'
            alignItems={{xs: 'flex-start', lg: 'center'}}
            spacing={2}
          >
            <Box sx={{minWidth: 0}}>
              <Typography
                sx={{
                  fontSize: {xs: '24px', md: '28px'},
                  lineHeight: '36px',
                  color: '#1A1916',
                  mb: 1,
                }}
              >
                <Translate expression='metrics.LeadFunnel.Title' />
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  lineHeight: '17px',
                  color: '#A8A49E',
                }}
              >
                <Translate expression='metrics.LeadFunnel.Subtitle' />
              </Typography>
            </Box>
            <Stack direction='row' alignItems='center' spacing={1.5} flexWrap='wrap' useFlexGap>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 500,
                  color: '#919191',
                }}
              >
                <Translate expression='metrics.LeadFunnel.VsPrior' />
              </Typography>
              <ButtonBase
                onClick={(event) => setDatePickerAnchorEl(event.currentTarget)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #EBEBEB',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                <CalendarTodayOutlined sx={{fontSize: 16, color: '#919191'}} />
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#6B6761',
                  }}
                >
                  {formattedDateRange}
                </Typography>
              </ButtonBase>
              {
                datePickerAnchorEl &&
                <Popover
                  open={Boolean(datePickerAnchorEl)}
                  anchorEl={datePickerAnchorEl}
                  onClose={() => setDatePickerAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1,
                        borderRadius: '12px',
                        border: '1px solid #E8E4DE',
                        boxShadow: '0px 12px 32px rgba(26, 25, 22, 0.12)',
                      },
                    },
                  }}
                >
                  <Box sx={{p: 1}}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DateCalendar
                        value={moment.tz(selectedCurrentDate, 'YYYY-MM-DD', timezone)}
                        maxDate={latestAvailableDate}
                        onChange={(nextDate) => {
                          if (!nextDate?.isValid?.()) {
                            return;
                          }

                          setSelectedCurrentDate(nextDate.clone().tz(timezone).format('YYYY-MM-DD'));
                          setDatePickerAnchorEl(null);
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Popover>
              }
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  p: 0.5,
                  backgroundColor: '#EEEEEE',
                  borderRadius: '8px',
                }}
              >
                <ButtonBase
                  onClick={() => setComparisonMode('DAILY')}
                  sx={{
                    px: 1.75,
                    py: 0.75,
                    borderRadius: '6px',
                    backgroundColor: comparisonMode === 'DAILY' ? '#FFFFFF' : 'transparent',
                    boxShadow: comparisonMode === 'DAILY' ? '0px 4px 4px rgba(0, 0, 0, 0.05)' : 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: comparisonMode === 'DAILY' ? '#000000' : '#6B6761',
                    }}
                  >
                    <Translate expression='metrics.LeadFunnel.Daily' />
                  </Typography>
                </ButtonBase>
                <ButtonBase
                  onClick={() => setComparisonMode('WEEKLY')}
                  sx={{
                    px: 1.75,
                    py: 0.75,
                    borderRadius: '6px',
                    backgroundColor: comparisonMode === 'WEEKLY' ? '#FFFFFF' : 'transparent',
                    boxShadow: comparisonMode === 'WEEKLY' ? '0px 4px 4px rgba(0, 0, 0, 0.05)' : 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: comparisonMode === 'WEEKLY' ? '#000000' : '#6B6761',
                    }}
                  >
                    <Translate expression='metrics.LeadFunnel.Weekly' />
                  </Typography>
                </ButtonBase>
              </Box>
            </Stack>
          </Stack>

          <KeyMetricsSection
            isLoading={logMetricsState.isLoading && !logMetricsState.hasLoadedOnce}
            errorMessage={logMetricsState.errorMessage}
            keyMetricCards={keyMetricCards}
            sectionTitleExpression='metrics.LeadFunnel.KeyMetricsSection'
          />

          <FunnelBreakdownSection
            isLoading={stageMetricsState.isLoading && !stageMetricsState.hasLoadedOnce}
            errorMessage={stageMetricsState.errorMessage}
            stageMissingDates={stageMissingDates}
            stageRows={stageRows}
            stageDistributionMax={stageDistributionMax}
          />

          <CohortSection
            chartHeight={340}
            isLoading={stageMetricsState.isLoading && !stageMetricsState.hasLoadedOnce}
            errorMessage={stageMetricsState.errorMessage}
            metricsByDate={stageMetricsState.metricsByDate}
            currentDate={cohortCurrentDate}
            previousDates={cohortPreviousDates}
          />
        </Stack>
      </Box>
    </Box>
  );
};

LeadFunnel.propTypes = {
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(LeadFunnel));
